<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import DashboardAppsAPI from 'dashboard/api/dashboardApps';
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import StructuredSnapshot from './StructuredSnapshot.vue';

const props = defineProps({
  dashboardApp: { type: Object, required: true },
  currentChat: { type: Object, default: () => ({}) },
  isVisible: { type: Boolean, default: false },
  position: { type: Number, required: true },
});

const FETCH_INFO_MESSAGE = 'chatwoot-dashboard-app:fetch-info';
const APP_CONTEXT_EVENT = 'appContext';
const DARK_THEME = 'dark';
const LIGHT_THEME = 'light';

const getCurrentTheme = () =>
  document.body.classList.contains(DARK_THEME) ? DARK_THEME : LIGHT_THEME;

const store = useStore();
const { t } = useI18n();
const hasOpenedAtLeastOnce = ref(false);
const iframeLoading = ref(true);
const snapshot = ref(null);
const unsuppressInProgress = ref(false);
const frameElements = ref([]);
const frameContexts = new WeakMap();
const currentTheme = ref(getCurrentTheme());
const contextRevision = ref(0);
let snapshotRequestId = 0;
let pollTimer;
let pollAttempts = 0;
let themeObserver;

const config = computed(() => props.dashboardApp.content || []);
const isStructuredSnapshot = computed(
  () => snapshot.value?.renderer === 'structured'
);
const rendererResolved = computed(() => Boolean(snapshot.value?.renderer));
const identityAllowed = computed(
  () => props.currentChat?.meta?.widget_identity_status !== 'visitor'
);
const contactId = computed(() => props.currentChat?.meta?.sender?.id);
const currentContact = computed(() =>
  store.getters['contacts/getContact'](contactId.value)
);
const contextKey = computed(() => {
  const contact = currentContact.value || {};
  // These two attributes are display outputs of the internal snapshot updater.
  const attributes = Object.entries(contact.custom_attributes || {})
    .filter(
      ([key]) =>
        !key.startsWith('cortex_') &&
        !['razo_social_nome', 'data_criao_usurio'].includes(key)
    )
    .sort(([left], [right]) => left.localeCompare(right));
  return JSON.stringify([
    props.dashboardApp.id,
    props.currentChat.id,
    props.currentChat.status,
    contactId.value,
    contact.name,
    contact.email,
    contact.phone_number,
    attributes,
  ]);
});
const currentAgent = computed(() => {
  const { id, name, email } = store.getters.getCurrentUser;
  return { id, name, email };
});
const customAttributes = computed(
  () => store.getters['attributes/getAttributes'] || []
);
const dashboardAppContext = computed(() => ({
  conversation: props.currentChat,
  contact: currentContact.value,
  currentAgent: currentAgent.value,
  customAttributes: customAttributes.value,
  theme: currentTheme.value,
}));

const postToFrame = (index, event, data) => {
  const frame = frameElements.value[index];
  if (!frame?.contentWindow || frameContexts.get(frame) !== contextKey.value)
    return;
  // External dashboard apps may use a sandboxed opaque origin. Browsers require
  // '*' as the target for that document; incoming messages remain restricted to
  // the exact iframe window below.
  frame.contentWindow.postMessage(JSON.stringify({ event, data }), '*');
};

const setFrameElement = (index, element) => {
  frameElements.value[index] = element;
  if (element) frameContexts.set(element, contextKey.value);
};

const deliverContext = index => {
  postToFrame(index, APP_CONTEXT_EVENT, dashboardAppContext.value);
  if (snapshot.value) {
    postToFrame(index, 'dashboardAppSnapshot', snapshot.value);
  }
  iframeLoading.value = false;
};

const deliverAppContextToFrames = () => {
  if (!props.isVisible) return;
  frameElements.value.forEach((_, index) => {
    postToFrame(index, APP_CONTEXT_EVENT, dashboardAppContext.value);
  });
};

const onThemeChange = () => {
  const theme = getCurrentTheme();
  if (theme === currentTheme.value) return;

  currentTheme.value = theme;
  deliverAppContextToFrames();
};

const beginSnapshotRequest = () => {
  snapshotRequestId += 1;
  return {
    id: snapshotRequestId,
    context: contextKey.value,
    appId: props.dashboardApp.id,
    conversationId: props.currentChat.id,
  };
};
const isCurrentRequest = request =>
  request.id === snapshotRequestId && request.context === contextKey.value;

const fetchSnapshot = async () => {
  if (!props.currentChat.id || !identityAllowed.value) return;
  const request = beginSnapshotRequest();

  try {
    const response = await DashboardAppsAPI.getSnapshot(
      request.appId,
      request.conversationId
    );
    if (!isCurrentRequest(request)) return;
    snapshot.value = response.data;
    frameElements.value.forEach((_, index) => deliverContext(index));
    if (
      ['pending', 'refreshing'].includes(response.data.status) &&
      pollAttempts < 8
    ) {
      pollAttempts += 1;
      pollTimer = window.setTimeout(fetchSnapshot, 2000);
    }
  } catch {
    if (!isCurrentRequest(request)) return;
    snapshot.value = {
      status: 'failed',
      result: null,
      payload: null,
      renderer: 'frame',
    };
    frameElements.value.forEach((_, index) => deliverContext(index));
  }
};

const forceRefreshSnapshot = async () => {
  if (!props.currentChat.id) return;
  const request = beginSnapshotRequest();
  window.clearTimeout(pollTimer);
  pollAttempts = 0;
  snapshot.value = {
    ...snapshot.value,
    status: 'pending',
    result: null,
    payload: null,
  };
  frameElements.value.forEach((_, index) => deliverContext(index));

  try {
    await DashboardAppsAPI.refreshSnapshot(
      request.appId,
      request.conversationId
    );
    if (!isCurrentRequest(request)) return;
    pollAttempts = 0;
    window.clearTimeout(pollTimer);
    pollTimer = window.setTimeout(fetchSnapshot, 500);
  } catch {
    if (!isCurrentRequest(request)) return;
    snapshot.value = {
      ...snapshot.value,
      status: 'failed',
      result: null,
      payload: null,
    };
    frameElements.value.forEach((_, index) => deliverContext(index));
  }
};

const unsuppressEmail = async () => {
  if (!props.currentChat.id || unsuppressInProgress.value) return;
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.UNSUPPRESS_CONFIRM')))
    return;

  unsuppressInProgress.value = true;
  const context = contextKey.value;
  const revision = contextRevision.value;
  const isCurrentContext = () =>
    context === contextKey.value && revision === contextRevision.value;
  try {
    await DashboardAppsAPI.unsuppressEmail(
      props.dashboardApp.id,
      props.currentChat.id
    );
    if (!isCurrentContext()) return;
    useAlert(t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.UNSUPPRESS_SUCCESS'));
    await forceRefreshSnapshot();
  } catch {
    if (!isCurrentContext()) return;
    useAlert(t('INBOX_MGMT.DATA_SOURCES.SNAPSHOT.UNSUPPRESS_ERROR'));
  } finally {
    if (isCurrentContext()) unsuppressInProgress.value = false;
  }
};

const onIframeMessage = event => {
  if (!props.isVisible) return;
  const index = frameElements.value.findIndex(
    frame => frame?.contentWindow === event.source
  );
  if (index < 0) return;
  if (frameContexts.get(frameElements.value[index]) !== contextKey.value)
    return;
  if (event.data === FETCH_INFO_MESSAGE) {
    deliverContext(index);
  } else if (event.data === 'chatplan-dashboard-app:refresh-snapshot') {
    forceRefreshSnapshot();
  }
};

watch(
  () => props.isVisible,
  visible => {
    if (!visible) return;
    hasOpenedAtLeastOnce.value = true;
    pollAttempts = 0;
    fetchSnapshot();
  },
  { immediate: true }
);

watch(contextKey, () => {
  contextRevision.value += 1;
  snapshotRequestId += 1;
  snapshot.value = null;
  frameElements.value = [];
  iframeLoading.value = true;
  unsuppressInProgress.value = false;
  pollAttempts = 0;
  window.clearTimeout(pollTimer);
  if (props.isVisible) fetchSnapshot();
});

watch(customAttributes, deliverAppContextToFrames);

onMounted(() => {
  window.addEventListener('message', onIframeMessage);
  themeObserver = new MutationObserver(onThemeChange);
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});
onBeforeUnmount(() => {
  snapshotRequestId += 1;
  contextRevision.value += 1;
  window.removeEventListener('message', onIframeMessage);
  themeObserver?.disconnect();
  window.clearTimeout(pollTimer);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="hasOpenedAtLeastOnce && !identityAllowed"
    class="flex h-full w-full items-center justify-center px-6 text-center text-sm text-n-slate-11"
  >
    {{ $t('DASHBOARD_APPS.IDENTITY_VERIFICATION_REQUIRED') }}
  </div>
  <div v-else-if="hasOpenedAtLeastOnce" class="h-full w-full">
    <LoadingState
      v-if="!rendererResolved"
      :message="$t('DASHBOARD_APPS.LOADING_MESSAGE')"
      class="flex h-full w-full items-center justify-center"
    />
    <StructuredSnapshot
      v-else-if="isStructuredSnapshot"
      :snapshot="snapshot"
      :action-in-progress="unsuppressInProgress"
      @refresh="forceRefreshSnapshot"
      @unsuppress-email="unsuppressEmail"
    />
    <template v-else>
      <div
        v-for="(configItem, index) in config"
        :key="configItem.url + '-' + index + '-' + contextRevision"
        class="h-full w-full"
      >
        <LoadingState
          v-if="iframeLoading"
          :message="$t('DASHBOARD_APPS.LOADING_MESSAGE')"
          class="flex h-full w-full items-center justify-center"
        />
        <iframe
          v-if="configItem.type === 'frame' && configItem.url"
          :id="'dashboard-app--frame-' + position + '-' + index"
          :ref="element => setFrameElement(index, element)"
          :src="configItem.url"
          class="h-full w-full border-0"
          allow="clipboard-write"
          sandbox="allow-downloads allow-forms allow-popups allow-scripts"
          @load="deliverContext(index)"
        />
      </div>
    </template>
  </div>
</template>
